import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, Select, Typography, Checkbox, PhoneNumber } from '@crisellevaldez/speculo-ui';
import { organizationSetupSchema, OrganizationSetupData } from '../schemas/onboarding.schema';
import { TIMEZONE_OPTIONS } from '../../../../constants/timezones';
import { US_STATES } from '../../../../constants/states';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { useAddressAutofill } from '../../../../hooks/address/useAddressAutofill';

interface Stage1OrganizationSetupProps {
  onNext: (data: OrganizationSetupData) => void;
  initialData?: Partial<OrganizationSetupData>;
  isLoading?: boolean;
}

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'Canada', label: 'Canada' },
];

export const Stage1OrganizationSetup: React.FC<Stage1OrganizationSetupProps> = ({
  onNext,
  initialData,
  isLoading = false,
}) => {
  const [useSameEmail, setUseSameEmail] = useState(false);
  const { sendWebhook } = useOnboardingWebhook();

  const form = useForm<OrganizationSetupData>({
    resolver: zodResolver(organizationSetupSchema),
    defaultValues: initialData || {
      org_name: '',
      client_address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
      org_phone: '',
      email: '',
      notification_email: '',
      timezone: '',
    },
  });

  const { handleZipCodeChange, isLookingUp } = useAddressAutofill({
    setValue: form.setValue,
    getValues: form.getValues,
  });

  const handleSubmit = async (data: OrganizationSetupData) => {
    sendWebhook('Stage 1 - Organization Setup', data); // Fire and forget
    onNext(data);
  };

  return (
    <StageContainer
      title="Organization Setup"
      description="Let's start with the basics. We need your organization details to set up your account and configure how Remi introduces your company to leads and clients."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6" noValidate>
        {/* Company/Agent Name */}
        <FormSection>
          <Form.Control required>
            <QuestionLabel>What is your company/agent name?</QuestionLabel>
            <Input
              {...form.register('org_name')}
              placeholder="ABC Realty"
              error={form.formState.errors.org_name?.message}
            />
            <TipBox>
              This is your business or agent name that will be used throughout the platform.
            </TipBox>
          </Form.Control>
        </FormSection>

        {/* Office Address */}
        <FormSection>
          <QuestionLabel> What is your office address?</QuestionLabel>

          <div className="space-y-4">
            <Form.Control required>
              <Form.Label>Street Address</Form.Label>
              <Input
                {...form.register('client_address.street')}
                placeholder="123 Main Street"
                error={form.formState.errors.client_address?.street?.message}
              />
            </Form.Control>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Zip Code</Form.Label>
                <Input
                  {...form.register('client_address.zip')}
                  placeholder="94102"
                  error={form.formState.errors.client_address?.zip?.message}
                  onChange={(e) => {
                    form.setValue('client_address.zip', e.target.value);
                    handleZipCodeChange(e.target.value);
                  }}
                  disabled={isLookingUp}
                />
                {isLookingUp && (
                  <p className="text-sm text-gray-500 mt-1">Looking up location...</p>
                )}
              </Form.Control>

              <Form.Control required>
                <Form.Label>City</Form.Label>
                <Input
                  {...form.register('client_address.city')}
                  placeholder="San Francisco"
                  error={form.formState.errors.client_address?.city?.message}
                />
              </Form.Control>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>State</Form.Label>
                <Controller
                  control={form.control}
                  name="client_address.state"
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={US_STATES}
                      placeholder="Select state"
                      error={form.formState.errors.client_address?.state?.message}
                      searchable
                    />
                  )}
                />
              </Form.Control>

              <Form.Control required>
                <Form.Label>Country</Form.Label>
                <Controller
                  control={form.control}
                  name="client_address.country"
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={COUNTRY_OPTIONS}
                      placeholder="Select country"
                      error={form.formState.errors.client_address?.country?.message}
                    />
                  )}
                />
              </Form.Control>
            </div>
          </div>
        </FormSection>

        {/* Contact Information */}
        <FormSection>
          <Form.Control required>
            <QuestionLabel>What is your office phone number?</QuestionLabel>
            <PhoneNumber
              value={form.watch('org_phone')}
              onChange={(value) => {
                form.setValue('org_phone', value || '', { shouldValidate: true });
              }}
              placeholder="Enter phone number"
              defaultCountry="US"
              error={form.formState.errors.org_phone?.message}
            />
            <TipBox>
              Your main phone number. This will be used on your website contact information section.
            </TipBox>
          </Form.Control>
        </FormSection>

        <FormSection>
          <Form.Control required>
            <QuestionLabel>What is your office email?</QuestionLabel>
            <Input
              {...form.register('email')}
              type="email"
              placeholder="contact@company.com"
              error={form.formState.errors.email?.message}
            />
            <TipBox>
              Your main contact email. This will be used on your website contact information
              section.
            </TipBox>
          </Form.Control>
        </FormSection>

        <FormSection>
          <Form.Control required={!useSameEmail}>
            <QuestionLabel>Email address for Notifications</QuestionLabel>

            <div className="space-y-3">
              <Checkbox
                id="useSameEmail"
                checked={useSameEmail}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseSameEmail(checked);
                  if (checked) {
                    const orgEmail = form.getValues('email');
                    form.setValue('notification_email', orgEmail);
                  }
                }}
                label="Use same email as organization email"
              />

              {!useSameEmail && (
                <Input
                  {...form.register('notification_email')}
                  type="email"
                  placeholder="notifications@company.com"
                  error={form.formState.errors.notification_email?.message}
                />
              )}
            </div>

            <TipBox>This email will receive new leads and AI calls notifications.</TipBox>
          </Form.Control>
        </FormSection>

        {/* Timezone */}
        <FormSection>
          <Form.Control required>
            <QuestionLabel>What time zone are you in?</QuestionLabel>
            <Controller
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <Select
                  {...field}
                  options={TIMEZONE_OPTIONS}
                  placeholder="Select your timezone"
                  error={form.formState.errors.timezone?.message}
                  searchable
                />
              )}
            />
          </Form.Control>
        </FormSection>

        {/* Submit Button */}
        <NextButton isLoading={isLoading} />
      </form>
    </StageContainer>
  );
};
